import { WithHyperlink } from "@src/components/elements/WithHyperlink";
import {
  CustomFormInput,
  validateCustomField,
} from "@src/pages/intake-requests/new/IntakeFormInput";
import {
  QuestionIdToValueMap,
  shouldShowField,
} from "@src/pages/intake-requests/new/useIntakeForm";
import { QuestionIdToQuestionConfigurationMap } from "@src/pages/intake-requests/new/useNewIntakeRequest";
import { encodeQuestionIdKey } from "@src/pages/vendor/AddNewVendor/useCustomVendorForm";
import { CreateIntakeSubmissionSchema } from "@src/sudozi-schemas/intake-submission-schemas";
import clsx from "clsx";
import React, { useEffect } from "react";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";

type WithCustomFormField = {
  customForm: CreateIntakeSubmissionSchema["steps"];
};

type Props<
  T extends WithCustomFormField,
  F extends "VENDOR" | "SERVICE" | "SERVICE_ITEM",
> = {
  formType: F extends "VENDOR" | "SERVICE" | "SERVICE_ITEM" ? F : never;
  className?: string;
  customQuestionIdToValueMap: QuestionIdToValueMap;
  customQuestionIdToConfigurationMap: QuestionIdToQuestionConfigurationMap;
  shouldSoftValidate?: boolean;
  customForm: CreateIntakeSubmissionSchema["steps"];
  setValue: T extends WithCustomFormField ? UseFormSetValue<T> : never;
  register: T extends WithCustomFormField ? UseFormRegister<T> : never;
} & (F extends "SERVICE_ITEM"
  ? {
      servicePeriodIndex: number;
    }
  : { servicePeriodIndex?: never });

export const CustomVendorForm = <
  T extends WithCustomFormField,
  F extends "VENDOR" | "SERVICE" | "SERVICE_ITEM",
>({
  className,
  customQuestionIdToValueMap,
  formType,
  customQuestionIdToConfigurationMap,
  shouldSoftValidate,
  servicePeriodIndex,
  customForm,
  setValue,
  register,
}: Props<T, F>) => {
  /**
   * Important to register every step and field in the custom form.
   * React-hook-form only stores in state what is registered / rendered in UI.
   * If the fields are not registered, step.fields will be undefined in the form state.
   */
  useEffect(() => {
    for (let i = 0; i < customForm.length; i++) {
      register(`customForm.${i}.fields`);
    }
  }, [customForm, register]);

  return (
    <div className={className}>
      {customForm.map((step, index) => {
        return (
          <CustomVendorFormFields
            key={index}
            stepIndex={index}
            step={step}
            customQuestionIdToValueMap={customQuestionIdToValueMap}
            customQuestionIdToConfigurationMap={
              customQuestionIdToConfigurationMap
            }
            shouldSoftValidate={shouldSoftValidate}
            className={clsx({
              hide:
                (formType === "VENDOR" && index !== 0) ||
                (formType === "SERVICE" && index !== 1) ||
                (formType === "SERVICE_ITEM" &&
                  index !== 2 + (servicePeriodIndex ?? 0)),
            })}
            setValue={setValue}
          />
        );
      })}
    </div>
  );
};

type CustomVendorFormFieldsProps<T extends WithCustomFormField> = {
  step: CreateIntakeSubmissionSchema["steps"][number];
  stepIndex: number;
  customQuestionIdToValueMap: QuestionIdToValueMap;
  customQuestionIdToConfigurationMap: QuestionIdToQuestionConfigurationMap;
  className?: string;
  shouldSoftValidate?: boolean;
  setValue: T extends WithCustomFormField ? UseFormSetValue<T> : never;
};
const CustomVendorFormFields = <T extends WithCustomFormField>({
  stepIndex,
  className,
  customQuestionIdToConfigurationMap,
  customQuestionIdToValueMap,
  shouldSoftValidate,
  step,
  setValue,
}: CustomVendorFormFieldsProps<T>) => {
  return (
    <div className={clsx(["column", "full-width", className])}>
      {step.fields.map((field, fieldIndex) => {
        const shouldShow = shouldShowField(
          encodeQuestionIdKey(field.questionId, stepIndex),
          customQuestionIdToConfigurationMap,
          customQuestionIdToValueMap,
        );
        return (
          <div
            // display: none used to prevent the flex container from adding a gap
            style={{
              display: shouldShow ? "flex" : "none",
            }}
            key={field.questionId}
            className="full-width column small-gap margin-ends-med"
          >
            {shouldShow ? (
              <>
                <div className="row">
                  <WithHyperlink
                    className="font-small label"
                    as="div"
                    str={field.label}
                  />
                  {field.isRequired && <span className="required">*</span>}
                </div>
                {field.description && (
                  <WithHyperlink
                    as="div"
                    className="font-small color-gray"
                    str={field.description}
                  />
                )}
              </>
            ) : null}
            <CustomFormInput
              field={field}
              hasError={
                shouldShow && shouldSoftValidate
                  ? !validateCustomField(field)
                  : false
              }
              name={`customForm.${stepIndex}.fields.${fieldIndex}.value`}
              onChange={(value) =>
                setValue(`customForm.${stepIndex}.fields.${fieldIndex}`, value)
              }
            />
          </div>
        );
      })}
    </div>
  );
};
